import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import {
  MapPin,
  LocateFixed,
  Camera,
  Calendar as CalIcon,
  ChevronLeft,
  ChevronRight,
  Save,
  ArrowLeft,
  Flame,
  Eye,
  EyeOff,
} from 'lucide-react';
import { PageShell } from '@/components/primitives/PageShell';
import { PageHeader } from '@/components/primitives/PageHeader';
import { Section } from '@/components/primitives/Section';
import { WizardStepper, type WizardStep } from '@/components/primitives/WizardStepper';
import { DateTimePicker } from '@/components/primitives/DateTimePicker';
import { MobileFormFooter } from '@/components/primitives/MobileFormFooter';
import { RoleGuard } from '@/components/primitives/RoleGuard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { useGeolocation } from '@/hooks/useGeolocation';
import { useDebouncedDraft } from '@/hooks/useDebouncedDraft';
import { getWildfire, createWildfire, updateWildfire } from '@/api/wildfires';
import { uploadImage } from '@/api/images';
import { listSuburbs } from '@/api/suburbs';
import {
  WildfireSchema,
  STEP_FIELDS,
  FIRE_TYPES,
  FIRE_TYPE_LABELS,
  type WildfireValues,
} from './WildfireSchema';
import { FirefighterPicker, type FirefighterRecord } from './FirefighterPicker';
import { MutualAidPicker, type AidProviderRecord } from './MutualAidPicker';
import { MemberOffenderPicker } from './MemberOffenderPicker';
import { cn } from '@/lib/utils';
import { parseApiError } from '@/lib/apiErrors';
import { Checkbox } from '@/components/ui/checkbox';

// The wizard's draw + drop-pin flow rides on top of the unified MapCanvas
// (settings gear, persisted tile layer, etc.). MapCanvas internally
// composes a FeatureGroup + react-leaflet-draw EditControl when its
// `drawing` prop is enabled — we still expose its FeatureGroup ref so
// the imperative fit-bounds-on-edit-load logic below keeps working.
import { Marker } from 'react-leaflet';
import L from 'leaflet';
import { MapCanvas } from '@/components/maps/MapCanvas';
import type { DrawnEvent } from '@/components/maps/MapDrawControl';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import PhotoCaptureRaw from '@/components/media/PhotoCapture';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PhotoCapture = PhotoCaptureRaw as any;

import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

const STEPS: WizardStep[] = [
  { id: 'location', label: 'Location' },
  { id: 'details', label: 'Details' },
  { id: 'damage', label: 'Damage' },
  { id: 'aid', label: 'Mutual aid' },
  { id: 'notes', label: 'Notes' },
  { id: 'review', label: 'Review' },
];

// Find the earliest wizard step that owns a given form field — used to jump
// the user to a server-reported error.
function stepIndexForField(field: string): number {
  for (let i = 0; i < STEPS.length; i++) {
    const s = STEPS[i];
    if (!s) continue;
    const fields = STEP_FIELDS[s.id as keyof typeof STEP_FIELDS] as
      | readonly string[]
      | undefined;
    if (fields?.includes(field)) return i;
  }
  return -1;
}

const DEFAULT_CENTER: [number, number] = [-25.6429, 28.343353];

// Stable EditControl props — define at module scope so the object identity
// stays the same across renders (otherwise react-leaflet-draw re-mounts the
// control mid-draw and cancels the in-progress polygon).
const DRAW_OPTIONS = {
  rectangle: false as const,
  circle: false as const,
  circlemarker: false as const,
  polyline: false as const,
  marker: { repeatMode: false },
  polygon: {
    allowIntersection: false,
    showArea: true,
    shapeOptions: {
      color: '#e64a19',
      fillColor: '#ff7d47',
      fillOpacity: 0.4,
      weight: 2,
    },
  },
};

// `edit` / `remove` MUST be option objects (or `false`) — never boolean
// `true`. leaflet-draw's EditToolbar runs `L.Util.extend(options.edit, …)`
// and throws `Cannot create property 'selectedPathOptions' on boolean 'true'`,
// which kills the whole draw toolbar. `{}` = enable with defaults.
const EDIT_OPTIONS = {
  poly: { allowIntersection: false },
  edit: {},
  remove: {},
};

// Default Leaflet marker icon (fixes the broken icon-url assumption).
const markerIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

interface PendingPhoto {
  id: string;
  file: File;
  url: string;
}

interface FirefighterOnSceneDTO {
  firefighter_id?: number | null;
  name?: string | null;
  surname?: string | null;
  guest_name?: string | null;
}

interface MutualAidOnSceneDTO {
  provider_key?: string | null;
  provider_kind?: 'fpa' | 'org' | null;
  provider_id?: number | null;
  provider_name?: string | null;
  org_type?: string | null;
  station_ids?: number[] | null;
  station_names?: string[] | null;
  // Pre-multi-select rows carry the singular pair; still read for back-compat.
  station_id?: number | null;
  station_name?: string | null;
  people?: number | null;
  vehicles?: number | null;
  vehicle_details?: string | null;
  hours?: number | null;
  // Derived server-side (people x hours); read-only, never submitted.
  man_hours?: number | null;
  note?: string | null;
}

interface WildfireDetailDTO {
  id: number;
  // The fire's OWN tenant. Edit mode must send this back rather than
  // whatever the top-bar switcher happens to be showing.
  tenant_id?: number | null;
  type?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  area_size_hectares?: number | null;
  suburb?: string | null;
  address?: string | null;
  people_involved?: number | null;
  firefighters?: FirefighterOnSceneDTO[] | null;
  mutual_aid?: MutualAidOnSceneDTO[] | null;
  man_hours?: number | null;
  injuries?: number | null;
  internal_notes?: string | null;
  external_notes?: string | null;
  possible_cause?: string | null;
  houses_damaged_count?: number | null;
  houses_estimated_damage?: number | null;
  livestock_injured?: number | null;
  livestock_estimated_damage?: number | null;
  fire_dept_ref?: string | null;
  police_ref?: string | null;
  is_illegal?: boolean;
  member_id?: number | null;
  parcel_id?: number | null;
  related_member_name?: string | null;
  is_active?: boolean;
  is_public?: boolean;
  location?: { coordinates: [number, number] } | null;
  area_polygon?: { coordinates: [number, number][][] } | null;
}

/**
 * Station ids on a stored mutual-aid row, tolerating the singular `station_id`
 * that rows written before the picker went multi-select still carry. Mirrors
 * `_station_ids_of` on the backend so neither side needs a backfill.
 */
function stationIdsOf(a: MutualAidOnSceneDTO): number[] {
  if (a.station_ids && a.station_ids.length) return a.station_ids;
  return a.station_id ? [a.station_id] : [];
}

/** The same, paired with names, for seeding the picker on edit. */
function seedStationsOf(a: MutualAidOnSceneDTO): { id: number; name: string }[] {
  const ids = stationIdsOf(a);
  const names =
    a.station_names && a.station_names.length
      ? a.station_names
      : a.station_name
        ? [a.station_name]
        : [];
  return ids.map((id, i) => ({ id, name: names[i] ?? `Station ${id}` }));
}

const draftKey = (uid: string | number | undefined | null) =>
  `wildfire-draft:${uid ?? 'anon'}`;

function defaultValues(): WildfireValues {
  return {
    fireType: 'veld',
    startTime: new Date(),
    endTime: null,
    areaSize: '' as unknown as number,
    suburb: '',
    address: '',
    peopleInvolved: 0,
    firefighterIds: [],
    guestFirefighters: [],
    manHours: 0,
    injuries: 0,
    possibleCause: '',
    housesDamagedCount: 0,
    housesEstimatedDamage: 0,
    livestockInjured: 0,
    livestockEstimatedDamage: 0,
    fireDeptRef: '',
    policeRef: '',
    isIllegal: false,
    memberId: null,
    memberName: '',
    parcelId: null,
    mutualAid: [],
    internalNotes: '',
    externalNotes: '',
    isActive: false,
    isPublic: true,
  };
}

function roundHectares(value: number): number {
  return Math.round(value * 100) / 100;
}

function truncateToMinute(d: Date): Date {
  return new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    d.getHours(),
    d.getMinutes(),
    0,
    0,
  );
}

export default function WildfireWizard() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser, can } = useAuth();
  const { tenantId } = useTenant();
  const isEdit = Boolean(id);
  // Seeded from the loaded wildfire in edit mode; null while creating.
  const [fireTenantId, setFireTenantId] = useState<number | null>(null);
  const [firefighterSeed, setFirefighterSeed] = useState<FirefighterRecord[]>([]);
  // Providers already named on the fire. Stored rows carry the resolved name,
  // so seeding from them keeps a since-retired organisation rendering by name
  // instead of collapsing to a bare id.
  const [aidProviderSeed, setAidProviderSeed] = useState<AidProviderRecord[]>([]);
  // Which FPA's firefighters the picker may offer. On edit that's the fire's
  // own tenant, so the list doesn't change under you when the top-bar
  // switcher moves. Null only for a super admin sitting on "All FPAs".
  const pickerTenantId = (isEdit ? fireTenantId : null) ?? tenantId ?? currentUser?.tenant_id ?? null;

  const [step, setStep] = useState(0);
  // Final-step explicit approval gate (groundwork for an admin-approval flow).
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [position, setPosition] = useState<[number, number]>(DEFAULT_CENTER);
  const [polygon, setPolygon] = useState<[number, number][] | null>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>(DEFAULT_CENTER);
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhoto[]>([]);
  // Tracks whether the user is in active leaflet-draw drawing mode. Used to
  // pause the MapCanvas auto-recenter so a delayed geolocation result doesn't
  // cancel the draw.
  const [drawing, setDrawing] = useState(false);
  // FeatureGroup ref handed off to MapCanvas via `drawing.featureGroupRef`.
  // We use it to pan/fitBounds to the loaded polygon on edit-mode open —
  // the polygon itself is seeded by MapCanvas's `initialGeometry`.
  const featureGroupRef = useRef<L.FeatureGroup | null>(null);
  // Tracks whether we've already fitted the map to the loaded polygon
  // (edit mode). Subsequent edits don't pan so the user's view is stable.
  const fittedToLoadedPolygonRef = useRef(false);
  // Map ref so the "Drop pin here" button can read the current center,
  // and so the edit-mode fitBounds can run once the map is ready.
  const [mapApi, setMapApi] = useState<L.Map | null>(null);
  const [suburbs, setSuburbs] = useState<{ id: number; name: string }[]>([]);
  const [loadingInit, setLoadingInit] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const draftPromptedRef = useRef(false);

  const form = useForm<WildfireValues>({
    resolver: zodResolver(WildfireSchema),
    defaultValues: defaultValues(),
    mode: 'onTouched',
  });

  const geo = useGeolocation();

  const draft = useDebouncedDraft<WildfireValues>({
    storageKey: draftKey(currentUser?.id),
    values: form.watch(),
    position,
    polygon,
    disabled: isEdit,
  });

  // ── Init: edit-mode load OR draft restore prompt OR geolocation ──────── //
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Load suburbs (best-effort)
      try {
        const data = (await listSuburbs({ page: 1, page_size: 100 })) as unknown as {
          suburbs?: { id: number; name: string }[];
        };
        if (!cancelled) setSuburbs(data.suburbs || []);
      } catch (err) {
        console.warn('Suburb load failed:', err);
      }

      if (isEdit && id) {
        try {
          const data = (await getWildfire(id)) as unknown as WildfireDetailDTO;
          if (cancelled) return;
          // Split the embedded firefighters list into id + guest arrays.
          // Keep the names too: the response carries them, and the picker
          // can otherwise only resolve whoever lands in the default first
          // page of results — everyone else rendered as a bare `#id`.
          const ffIds: number[] = [];
          const ffGuests: string[] = [];
          const ffSeed: FirefighterRecord[] = [];
          for (const f of data.firefighters ?? []) {
            if (f.firefighter_id != null) {
              ffIds.push(f.firefighter_id);
              ffSeed.push({
                id: f.firefighter_id,
                name: f.name ?? '',
                surname: f.surname ?? '',
              });
            } else if (f.guest_name) ffGuests.push(f.guest_name);
          }
          setFirefighterSeed(ffSeed);
          setAidProviderSeed(
            (data.mutual_aid ?? [])
              .filter((a) => a.provider_key)
              .map((a) => ({
                key: a.provider_key as string,
                kind: (a.provider_kind ?? 'fpa') as 'fpa' | 'org',
                id: a.provider_id ?? 0,
                name: a.provider_name ?? `#${a.provider_id ?? ''}`,
                org_type: a.org_type ?? null,
                stations: seedStationsOf(a),
              })),
          );
          setFireTenantId(data.tenant_id ?? null);
          // Legacy rows may still carry the retired `illegal_burn` type; treat
          // them as a veld fire flagged illegal.
          const legacyIllegal =
            (data.type as string | null | undefined) === 'illegal_burn';
          form.reset({
            fireType: (legacyIllegal
              ? 'veld'
              : (data.type as WildfireValues['fireType'] | null | undefined)) ?? 'veld',
            startTime: data.start_time ? new Date(data.start_time) : new Date(),
            endTime: data.end_time ? new Date(data.end_time) : null,
            areaSize: (data.area_size_hectares != null
              ? roundHectares(data.area_size_hectares)
              : '') as unknown as number,
            suburb: data.suburb ?? '',
            address: data.address ?? '',
            peopleInvolved: data.people_involved ?? 0,
            firefighterIds: ffIds,
            guestFirefighters: ffGuests,
            manHours: data.man_hours ?? 0,
            injuries: data.injuries ?? 0,
            possibleCause: data.possible_cause ?? '',
            housesDamagedCount: data.houses_damaged_count ?? 0,
            housesEstimatedDamage: data.houses_estimated_damage ?? 0,
            livestockInjured: data.livestock_injured ?? 0,
            livestockEstimatedDamage: data.livestock_estimated_damage ?? 0,
            fireDeptRef: data.fire_dept_ref ?? '',
            policeRef: data.police_ref ?? '',
            isIllegal: data.is_illegal ?? legacyIllegal,
            memberId: data.member_id ?? null,
            memberName: data.related_member_name ?? '',
            parcelId: data.parcel_id ?? null,
            mutualAid: (data.mutual_aid ?? []).map((a) => ({
              providerKey: a.provider_key ?? '',
              providerKind: (a.provider_kind ?? 'fpa') as 'fpa' | 'org',
              providerId: a.provider_id ?? 0,
              providerName: a.provider_name ?? '',
              stationIds: stationIdsOf(a),
              people: a.people ?? 0,
              vehicles: a.vehicles ?? 0,
              vehicleDetails: a.vehicle_details ?? '',
              hours: a.hours ?? 0,
              note: a.note ?? '',
            })),
            internalNotes: data.internal_notes ?? '',
            externalNotes: data.external_notes ?? '',
            isActive: data.is_active ?? false,
            isPublic: data.is_public ?? true,
          });
          if (data.location) {
            const [lng, lat] = data.location.coordinates;
            setPosition([lat, lng]);
            setMapCenter([lat, lng]);
          }
          if (data.area_polygon) {
            const poly = data.area_polygon.coordinates[0]?.map(
              ([lng, lat]) => [lat, lng] as [number, number],
            );
            if (poly && poly.length > 0) setPolygon(poly);
          }
        } catch (err) {
          console.error('Failed to load wildfire:', err);
          if (!cancelled) setError('Failed to load wildfire. Please try again.');
        } finally {
          if (!cancelled) setLoadingInit(false);
        }
        return;
      }

      // Create-mode: try draft restore prompt, then geolocation.
      if (!draftPromptedRef.current) {
        draftPromptedRef.current = true;
        const restored = draft.restore();
        if (restored?.values) {
          const ago = restored.savedAt
            ? Math.round((Date.now() - restored.savedAt) / 60000)
            : null;
          toast(`Restore unsaved draft${ago != null ? ` from ${ago} min ago` : ''}?`, {
            duration: 10000,
            action: {
              label: 'Restore',
              onClick: () => {
                const vals = restored.values as WildfireValues;
                // Back-compat: an older draft may carry the retired
                // `illegal_burn` fireType — coerce it to veld + the flag so the
                // enum-validated form doesn't reject it on submit.
                if ((vals.fireType as string) === 'illegal_burn') {
                  vals.fireType = 'veld';
                  vals.isIllegal = true;
                }
                if (vals.isIllegal == null) vals.isIllegal = false;
                // Revive Date instances from ISO strings if needed
                if (vals.startTime) vals.startTime = new Date(vals.startTime);
                if (vals.endTime) vals.endTime = new Date(vals.endTime);
                form.reset(vals);
                if (restored.position) {
                  setPosition(restored.position);
                  setMapCenter(restored.position);
                }
                if (restored.polygon) setPolygon(restored.polygon);
                toast.success('Draft restored');
              },
            },
            cancel: {
              label: 'Discard',
              onClick: () => {
                draft.discard();
                toast('Draft discarded');
              },
            },
          });
        }
      }

      // Auto-geolocation on create
      const pos = await geo.capture();
      if (cancelled) return;
      if (pos) {
        setPosition([pos.lat, pos.lng]);
        setMapCenter([pos.lat, pos.lng]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  // ── Auto-compute manHours from startTime + endTime + peopleInvolved ──── //
  const startTime = form.watch('startTime');
  const endTime = form.watch('endTime');
  const peopleInvolved = form.watch('peopleInvolved');
  useEffect(() => {
    if (!startTime || !endTime || !peopleInvolved) return;
    const a = truncateToMinute(startTime);
    const b = truncateToMinute(endTime);
    const hours = (b.getTime() - a.getTime()) / 3_600_000;
    if (hours <= 0) return;
    const mh = hours * Number(peopleInvolved);
    form.setValue('manHours', Number(mh.toFixed(2)), { shouldValidate: false });
  }, [startTime, endTime, peopleInvolved, form]);

  // ── Polygon draw handlers ──────────────────────────────────────────── //
  // MapCanvas's drawing.{onCreated,onEdited} hand us a DrawnEvent with the
  // [lat, lng] positions + the geodesic area already computed.
  const handleDrawCreated = useCallback(
    (e: DrawnEvent) => {
      setDrawing(false);
      // Marker (drop-pin) tool: set the fire location. The transient drawn
      // marker is removed in MapDrawControl, so the draggable <Marker> stays
      // the single managed pin.
      if (e.marker) {
        setPosition(e.marker);
        return;
      }
      if (!e.positions) return;
      setPolygon(e.positions);
      if (e.areaHectares != null) {
        form.setValue('areaSize', roundHectares(e.areaHectares), {
          shouldValidate: false,
        });
      }
    },
    [form],
  );
  const handleDrawEdited = useCallback(
    (e: DrawnEvent) => {
      if (e.positions) setPolygon(e.positions);
      if (e.areaHectares != null) {
        form.setValue('areaSize', roundHectares(e.areaHectares), {
          shouldValidate: false,
        });
      }
    },
    [form],
  );
  const handleDrawDeleted = useCallback(
    (e: { allCleared: boolean }) => {
      if (e.allCleared) setPolygon(null);
    },
    [],
  );

  // Convert the saved polygon (lat/lng array) to a GeoJSON Polygon so
  // MapCanvas can seed it as an editable layer in edit mode.
  const initialPolygonGeometry = useMemo<GeoJSON.Polygon | null>(() => {
    if (!isEdit) return null;
    if (!polygon || polygon.length === 0) return null;
    return {
      type: 'Polygon',
      coordinates: [polygon.map(([lat, lng]) => [lng, lat] as [number, number])],
    };
    // Intentionally key on a stable identity (polygon length) so the seed
    // doesn't re-run mid-edit when leaflet-draw mutates the layer in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, polygon?.length]);

  // Pan/fitBounds to the loaded polygon once the map is ready — fires only
  // once per page load so subsequent vertex drags don't jolt the view.
  useEffect(() => {
    if (!isEdit || !mapApi || fittedToLoadedPolygonRef.current) return;
    if (!polygon || polygon.length === 0) return;
    try {
      const bounds = L.latLngBounds(polygon as L.LatLngExpression[]);
      if (bounds.isValid()) {
        mapApi.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
        fittedToLoadedPolygonRef.current = true;
      }
    } catch (err) {
      console.warn('Failed to fit map to loaded polygon:', err);
    }
  }, [isEdit, mapApi, polygon]);

  // ── Use my location button ─────────────────────────────────────────── //
  const onUseMyLocation = async () => {
    const pos = await geo.capture();
    if (pos) {
      setPosition([pos.lat, pos.lng]);
      setMapCenter([pos.lat, pos.lng]);
      toast.success('Location captured');
    }
  };

  // ── Step navigation ────────────────────────────────────────────────── //
  const next = async () => {
    const stepDef = STEPS[step];
    const fields = ((stepDef &&
      STEP_FIELDS[stepDef.id as keyof typeof STEP_FIELDS]) ||
      []) as readonly string[];
    if (fields.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ok = await form.trigger(fields as any);
      if (!ok) {
        toast.error('Please fix the highlighted fields');
        return;
      }
    }
    // Step 1 (Location) needs a position
    if (step === 0 && !position) {
      toast.error('Set the wildfire location on the map');
      return;
    }
    setStep(Math.min(step + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const back = () => {
    setStep(Math.max(step - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /**
   * Upload whatever the user staged in PhotoCapture. Runs on save in BOTH
   * modes: on create the id only exists once the POST returns, and on edit
   * we deliberately wait rather than uploading on file-pick, so abandoning
   * an edit leaves nothing behind.
   */
  const uploadPendingPhotos = async (wildfireId: number) => {
    if (pendingPhotos.length === 0) return;
    const results = await Promise.allSettled(
      pendingPhotos.map((p) => uploadImage(wildfireId, p.file)),
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed > 0) {
      console.warn(`${failed} photo(s) failed to upload.`);
      toast.warning(`${failed} photo(s) failed to upload — try again from this form.`);
    } else {
      setPendingPhotos([]);
    }
  };

  // ── Submit ─────────────────────────────────────────────────────────── //
  const onSubmit = async (values: WildfireValues) => {
    if (!position) {
      setError('Please set the wildfire location on the map');
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      // On edit the fire's stored tenant wins. Reading the global switcher
      // instead let a super admin silently reassign a fire to whichever FPA
      // the top bar happened to be on — update_wildfire only validates the
      // EXISTING record's tenant, so the rewrite went through unchallenged.
      const resolvedTenantId = isEdit
        ? fireTenantId ?? tenantId ?? currentUser?.tenant_id ?? null
        : tenantId ?? currentUser?.tenant_id ?? null;
      if (!isEdit && !resolvedTenantId) {
        setError(
          'Please select an FPA from the top bar before reporting a wildfire.',
        );
        setSubmitting(false);
        return;
      }

      const payload: Record<string, unknown> = {
        tenant_id: resolvedTenantId,
        type: values.fireType,
        location: {
          type: 'Point',
          coordinates: [position[1], position[0]],
        },
        area_polygon: polygon
          ? {
              type: 'Polygon',
              coordinates: [polygon.map(([lat, lng]) => [lng, lat])],
            }
          : null,
        area_size_hectares:
          values.areaSize !== undefined &&
          values.areaSize !== null &&
          (values.areaSize as unknown) !== ''
            ? roundHectares(Number(values.areaSize))
            : null,
        suburb: values.suburb || null,
        address: values.address || null,
        start_time: truncateToMinute(values.startTime).toISOString(),
        end_time: values.endTime ? truncateToMinute(values.endTime).toISOString() : null,
        people_involved: values.peopleInvolved || 0,
        firefighter_ids: values.firefighterIds || [],
        guest_firefighters: values.guestFirefighters || [],
        man_hours: values.manHours || null,
        internal_notes: values.internalNotes || null,
        external_notes: values.externalNotes || null,
        injuries: values.injuries || 0,
        possible_cause: values.possibleCause || null,
        houses_damaged_count: values.housesDamagedCount || 0,
        houses_estimated_damage: values.housesEstimatedDamage || null,
        livestock_injured: values.livestockInjured || 0,
        livestock_estimated_damage: values.livestockEstimatedDamage || null,
        fire_dept_ref: values.fireDeptRef || null,
        police_ref: values.policeRef || null,
        // Illegal flag + optional member linkage — available on any fire.
        is_illegal: values.isIllegal,
        // Replace-all on the server. Sent even when empty so clearing the
        // section actually clears it rather than reading as "untouched".
        mutual_aid: (values.mutualAid || [])
          .filter((row) => row.providerKey)
          .map((row) => ({
            provider_kind: row.providerKind,
            provider_id: row.providerId,
            station_ids: row.stationIds ?? [],
            people: row.people ?? 0,
            vehicles: row.vehicles ?? 0,
            vehicle_details: row.vehicleDetails || null,
            hours: row.hours ?? 0,
            note: row.note || null,
          })),
        member_id: values.memberId ?? null,
        parcel_id: values.parcelId ?? null,
        related_member_name: values.memberName || null,
        is_active: values.isActive,
        is_public: values.isPublic,
      };

      if (isEdit && id) {
        await updateWildfire(id, payload);
        await uploadPendingPhotos(Number(id));
        toast.success('Wildfire updated');
        navigate(`/wildfires/${id}`);
      } else {
        const created = (await createWildfire(payload)) as { id?: number };
        const newId = created?.id;
        if (newId) await uploadPendingPhotos(newId);
        draft.discard();
        toast.success('Wildfire reported');
        if (newId) navigate(`/wildfires/${newId}`);
        else navigate('/wildfires');
      }
    } catch (err) {
      console.error('Submit failed:', err);
      const parsed = parseApiError(
        err,
        `Failed to ${isEdit ? 'update' : 'create'} wildfire`,
      );
      // Highlight each offending field inline and jump back to the earliest
      // step that owns one, so the user can actually find and fix it.
      let jumpStep = -1;
      for (const fe of parsed.fieldErrors) {
        form.setError(fe.field as keyof WildfireValues, {
          type: 'server',
          message: fe.msg,
        });
        const s = stepIndexForField(fe.field);
        if (s !== -1 && (jumpStep === -1 || s < jumpStep)) jumpStep = s;
      }
      if (jumpStep !== -1) {
        setStep(jumpStep);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      setError(parsed.message);
      toast.error(
        parsed.fieldErrors.length > 0
          ? 'Please fix the highlighted fields'
          : parsed.message,
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingInit) {
    return (
      <PageShell>
        <Skeleton className="mb-3 h-8 w-1/3" />
        <Skeleton className="mb-2 h-12 w-full" />
        <Skeleton className="h-96 w-full" />
      </PageShell>
    );
  }

  const isLastStep = step === STEPS.length - 1;
  const cancelTo = isEdit && id ? `/wildfires/${id}` : '/wildfires';

  return (
    <PageShell noBottomPad>
      <PageHeader
        title={isEdit ? `Edit wildfire #${id}` : 'Report wildfire'}
        breadcrumbs={[
          { label: 'Wildfires', to: '/wildfires' },
          { label: isEdit ? `#${id}` : 'New' },
        ]}
        actions={
          <Button asChild variant="ghost" size="sm" className="hidden md:inline-flex">
            <Link to={cancelTo}>
              <ArrowLeft className="mr-1 h-4 w-4" />
              Cancel
            </Link>
          </Button>
        }
      />

      <WizardStepper
        steps={STEPS}
        currentStep={step}
        onStepClick={setStep}
        className="mb-6"
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription className="whitespace-pre-line">{error}</AlertDescription>
        </Alert>
      )}

      <FormProvider {...form}>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* STEP 1 — Location & photos */}
            <div className={cn(step !== 0 && 'hidden')}>
              <Section
                title="Location"
                description="Pan the map to centre on the fire, then tap “Drop pin here”. Use the burn-area tool (top-right) to outline the area burnt. The pin can also be dragged."
                actions={
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (!mapApi) return;
                        const c = mapApi.getCenter();
                        setPosition([c.lat, c.lng]);
                        toast.success('Pin dropped at map centre');
                      }}
                      disabled={!mapApi}
                    >
                      <MapPin className="mr-2 h-4 w-4" />
                      Drop pin here
                    </Button>
                    {!isEdit && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={onUseMyLocation}
                        disabled={geo.loading}
                      >
                        <LocateFixed className="mr-2 h-4 w-4" />
                        {geo.loading ? 'Locating…' : 'Use my location'}
                      </Button>
                    )}
                  </div>
                }
              >
                {geo.error && (
                  <Alert className="mb-3">
                    <AlertDescription>{geo.error}</AlertDescription>
                  </Alert>
                )}

                <div className="relative h-[60vh] min-h-[360px] w-full">
                  <MapCanvas
                    center={mapCenter}
                    zoom={isEdit ? 13 : 10}
                    variant="lg"
                    tenantId={tenantId ?? null}
                    className="!h-full !min-h-0"
                    mapSearch
                    /* Pause auto-recenter while drawing — leaflet-draw cancels
                       in-flight draws on map.setView(). */
                    recenterTo={drawing ? null : mapCenter}
                    onMapReady={setMapApi}
                    /* Parcels are context-only while plotting a fire: disable
                       interactivity so clicks plot the polygon, never select a
                       parcel. */
                    parcelInteractive={false}
                    drawing={{
                      enabled: true,
                      initialGeometry: initialPolygonGeometry,
                      drawOptions: DRAW_OPTIONS,
                      editOptions: EDIT_OPTIONS,
                      onCreated: handleDrawCreated,
                      onEdited: handleDrawEdited,
                      onDeleted: handleDrawDeleted,
                      onDrawingStateChange: setDrawing,
                      featureGroupRef,
                    }}
                  >
                    <Marker
                      position={position}
                      icon={markerIcon}
                      draggable
                      eventHandlers={{
                        dragend: (e) => {
                          const ll = (e.target as L.Marker).getLatLng();
                          setPosition([ll.lat, ll.lng]);
                        },
                      }}
                    />
                  </MapCanvas>
                </div>

                <div className="mt-3 grid gap-2 sm:grid-cols-3">
                  <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
                    <div className="text-xs text-muted-foreground">Latitude</div>
                    <div className="font-mono text-sm">
                      {position ? position[0].toFixed(6) : '—'}
                    </div>
                  </div>
                  <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
                    <div className="text-xs text-muted-foreground">Longitude</div>
                    <div className="font-mono text-sm">
                      {position ? position[1].toFixed(6) : '—'}
                    </div>
                  </div>
                  <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
                    <div className="text-xs text-muted-foreground">Burn area</div>
                    <div className="font-mono text-sm">
                      {polygon ? `${polygon.length} points` : '— none'}
                    </div>
                  </div>
                </div>
              </Section>

              {/* Shown when editing too: the upload endpoint has no
                  permission gate, this was only ever hidden by the UI, and
                  nothing else in the app can attach media to an existing
                  fire. Files are still held until submit, so abandoning an
                  edit uploads nothing. */}
              <Section
                title="Photos"
                description={
                  isEdit
                    ? 'Optional — add more photos. Uploaded when you save.'
                    : 'Optional — capture or upload photos. Saved with the report when you submit.'
                }
              >
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Camera className="h-4 w-4" />
                  Add photos
                </div>
                <div className="mt-2">
                  <PhotoCapture photos={pendingPhotos} onChange={setPendingPhotos} />
                </div>
              </Section>
            </div>

            {/* STEP 2 — Details */}
            <div className={cn(step !== 1 && 'hidden')}>
              <Section title="Time">
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="startTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Start time <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <DateTimePicker
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Pick start"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="endTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>End time</FormLabel>
                        <FormControl>
                          <DateTimePicker
                            value={field.value ?? null}
                            onChange={field.onChange}
                            placeholder="Ongoing"
                            clearable
                          />
                        </FormControl>
                        <FormDescription>Leave blank if still active.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </Section>

              <Section title="Area & location">
                <div className="grid gap-4 sm:grid-cols-3">
                  <FormField
                    control={form.control}
                    name="areaSize"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Area size (hectares)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={(field.value as unknown as string | number | undefined) ?? ''}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value === ''
                                  ? ''
                                  : Number(e.target.value),
                              )
                            }
                          />
                        </FormControl>
                        <FormDescription>
                          Auto-filled when you draw a burn area.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="suburb"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Suburb</FormLabel>
                        <FormControl>
                          <Input
                            list="suburbs-datalist"
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            placeholder="Type or pick from list"
                          />
                        </FormControl>
                        <datalist id="suburbs-datalist">
                          {suburbs.map((s) => (
                            <option key={s.id} value={s.name} />
                          ))}
                        </datalist>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address</FormLabel>
                        <FormControl>
                          <Input
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            placeholder="Street address or nearest landmark"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </Section>

              <Section title="Type">
                <FormField
                  control={form.control}
                  name="fireType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fire type</FormLabel>
                      <FormControl>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                          {FIRE_TYPES.map((t) => {
                            const active = field.value === t;
                            return (
                              <button
                                key={t}
                                type="button"
                                onClick={() => field.onChange(t)}
                                className={cn(
                                  'rounded-md border px-3 py-2 text-sm font-medium transition',
                                  active
                                    ? 'border-primary bg-primary/10 text-primary'
                                    : 'border-border bg-card text-muted-foreground hover:bg-muted',
                                )}
                              >
                                {FIRE_TYPE_LABELS[t]}
                              </button>
                            );
                          })}
                        </div>
                      </FormControl>
                      <FormDescription>
                        Only <strong>Veld</strong> fires contribute to GUFPA statistics.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="isIllegal"
                  render={({ field }) => (
                    <FormItem className="mt-4 flex items-start gap-3 space-y-0 rounded-md border border-border bg-muted/30 p-3">
                      <FormControl>
                        <Checkbox
                          checked={Boolean(field.value)}
                          onCheckedChange={field.onChange}
                          className="mt-0.5"
                        />
                      </FormControl>
                      <div>
                        <FormLabel>Illegal / unauthorised burn</FormLabel>
                        <FormDescription>
                          Tick if this burn was illegal — applies to any fire type
                          (even veld) and does not affect GUFPA statistics.
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />

                {/* The picker searches members by name, so it's member data:
                    hidden from roles without fpa_member_list. Without this it
                    renders "No members match" on a 403 — reporting a
                    permission failure as no-data. `memberId` is optional, so
                    validation still passes and an existing link round-trips.
                    Cosmetic only — the server drops member fields it won't
                    serve back (see _denormalize_member_name). */}
                <RoleGuard can={{ resource: 'fpa_member', action: 'list' }}>
                  <FormField
                    control={form.control}
                    name="memberId"
                    render={() => (
                      <FormItem className="mt-4">
                        <FormLabel>Linked FPA member (optional)</FormLabel>
                        <FormControl>
                          <MemberOffenderPicker />
                        </FormControl>
                        <FormDescription>
                          Optionally link this fire to an FPA member so it appears on
                          their record.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </RoleGuard>
              </Section>

              <Section title="Status">
                <FormField
                  control={form.control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between gap-3 space-y-0">
                      <div>
                        <FormLabel>Currently active</FormLabel>
                        <FormDescription>Mark on if the wildfire hasn't been resolved yet.</FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={Boolean(field.value)}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </Section>
            </div>

            {/* STEP 3 — Damage */}
            <div className={cn(step !== 2 && 'hidden')}>
              <Section
                title="Firefighters on scene"
                description="Pick known firefighters and / or add guest names. The count auto-feeds into man hours."
              >
                {pickerTenantId == null ? (
                  <p className="text-sm text-muted-foreground">
                    Select an FPA in the top bar to choose firefighters.
                  </p>
                ) : (
                  <FirefighterPicker
                    tenantId={pickerTenantId}
                    seedKnown={firefighterSeed}
                  />
                )}
              </Section>
              <Section title="Time & injuries">
                <div className="grid gap-4 sm:grid-cols-2">
                  <NumField name="manHours" label="Man hours" description="Auto-computed from time × people" readOnly />
                  <NumField name="injuries" label="Injuries" />
                </div>
              </Section>
              <Section title="Cause">
                <FormField
                  control={form.control}
                  name="possibleCause"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Possible cause</FormLabel>
                      <FormControl>
                        <Textarea {...field} rows={3} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </Section>
              <Section title="House damage">
                <div className="grid gap-4 sm:grid-cols-2">
                  <NumField name="housesDamagedCount" label="Houses damaged (count)" />
                  <NumField name="housesEstimatedDamage" label="Estimated damage (R)" step="0.01" />
                </div>
              </Section>
              <Section title="Livestock damage">
                <div className="grid gap-4 sm:grid-cols-2">
                  <NumField name="livestockInjured" label="Livestock injured (count)" />
                  <NumField name="livestockEstimatedDamage" label="Estimated damage (R)" step="0.01" />
                </div>
              </Section>
              <Accordion type="single" collapsible defaultValue="refs">
                <AccordionItem value="refs">
                  <AccordionTrigger className="text-base font-semibold">
                    Reference numbers (optional)
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid gap-4 pt-2 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="fireDeptRef"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Fire Dept reference</FormLabel>
                            <FormControl>
                              <Input {...field} value={field.value ?? ''} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="policeRef"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Police reference</FormLabel>
                            <FormControl>
                              <Input {...field} value={field.value ?? ''} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            {/* STEP 4 — Mutual aid */}
            <div className={cn(step !== 3 && 'hidden')}>
              <Section title="Assistance received">
                {pickerTenantId == null ? (
                  <p className="text-sm text-muted-foreground">
                    Select an FPA in the top bar to record assistance.
                  </p>
                ) : (
                  <MutualAidPicker
                    tenantId={pickerTenantId}
                    seedProviders={aidProviderSeed}
                  />
                )}
              </Section>
            </div>

            {/* STEP 5 — Notes & visibility */}
            <div className={cn(step !== 4 && 'hidden')}>
              <Section title="Photos">
                <PhotoCapture photos={pendingPhotos} onChange={setPendingPhotos} />
              </Section>

              <Section title="Notes">
                <FormField
                  control={form.control}
                  name="internalNotes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Internal notes</FormLabel>
                      <FormDescription>Visible only to staff.</FormDescription>
                      <FormControl>
                        <Textarea {...field} rows={3} value={field.value ?? ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="externalNotes"
                  render={({ field }) => (
                    <FormItem className="mt-4">
                      <FormLabel>External notes</FormLabel>
                      <FormDescription>Shown publicly when the report is public.</FormDescription>
                      <FormControl>
                        <Textarea {...field} rows={3} value={field.value ?? ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </Section>

              <Section title="Visibility">
                <FormField
                  control={form.control}
                  name="isPublic"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between gap-3 space-y-0">
                      <div>
                        <FormLabel>Visible on the public map</FormLabel>
                        <FormDescription>
                          Turn off to keep this report internal to staff.
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={Boolean(field.value)}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </Section>
            </div>

            {/* STEP 6 — Review & confirm */}
            <div className={cn(step !== 5 && 'hidden')}>
              <Section
                title="Summary"
                description="Check the details below, then confirm to submit the report."
              >
                <ReviewSummary
                  values={form.getValues()}
                  position={position}
                  polygon={polygon}
                  photos={pendingPhotos.length}
                />
              </Section>

              <Section title="Confirm & submit">
                {!isEdit && !can('wildfire', 'approve') && (
                  <Alert className="mb-3">
                    <AlertDescription>
                      This report will be saved as a <strong>draft</strong> and
                      submitted to an administrator for approval before it
                      appears in stats and on the public map.
                    </AlertDescription>
                  </Alert>
                )}
                <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border bg-muted/30 p-3">
                  <Checkbox
                    checked={reviewConfirmed}
                    onCheckedChange={(v) => setReviewConfirmed(Boolean(v))}
                    className="mt-0.5"
                  />
                  <span className="text-sm">
                    I have reviewed this report and confirm the details are
                    accurate.
                  </span>
                </label>
              </Section>
            </div>

            {/* Desktop inline action row at the bottom of every step */}
            <div className="hidden items-center justify-between gap-2 pb-6 md:flex">
              <div className="text-sm text-muted-foreground">
                {draft.savedAt ? (
                  <span>
                    Draft saved {new Date(draft.savedAt).toLocaleTimeString()}
                  </span>
                ) : null}
              </div>
              <div className="flex gap-2">
                {step > 0 && (
                  <Button type="button" variant="outline" onClick={back} disabled={submitting}>
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    Back
                  </Button>
                )}
                {!isLastStep ? (
                  <Button type="button" onClick={next} disabled={submitting}>
                    Next
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                ) : (
                  <Button type="submit" disabled={submitting || !reviewConfirmed}>
                    <Save className="mr-1 h-4 w-4" />
                    {submitting
                      ? 'Saving…'
                      : isEdit
                        ? 'Save changes'
                        : 'Save report'}
                  </Button>
                )}
              </div>
            </div>

            <MobileFormFooter
              isSubmitting={submitting}
              saveDisabled={isLastStep && !reviewConfirmed}
              cancelLabel={step === 0 ? 'Cancel' : '← Back'}
              onCancel={step === 0 ? () => navigate(cancelTo) : back}
              saveLabel={
                isLastStep ? (isEdit ? 'Save changes' : 'Save report') : 'Next →'
              }
              submitType={isLastStep ? 'submit' : 'button'}
              onSubmit={isLastStep ? undefined : next}
            />
          </form>
        </Form>
      </FormProvider>
    </PageShell>
  );
}

/* ── Small helpers ─────────────────────────────────────────────────────── */

interface NumFieldProps {
  name: keyof WildfireValues;
  label: string;
  description?: string;
  readOnly?: boolean;
  step?: string;
}

function NumField({ name, label, description, readOnly, step }: NumFieldProps) {
  const form = useFormCtx();
  return (
    <FormField
      control={form.control}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      name={name as any}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              type="number"
              min="0"
              step={step ?? '1'}
              readOnly={readOnly}
              value={(field.value as unknown as string | number | undefined) ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? 0 : Number(e.target.value))
              }
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

// Tiny hook to access the form from the wizard children — we use FormProvider
// at the top, so `useFormContext` works inside helpers.
import { useFormContext } from 'react-hook-form';
function useFormCtx() {
  return useFormContext<WildfireValues>();
}

interface ReviewSummaryProps {
  values: WildfireValues;
  position: [number, number] | null;
  polygon: [number, number][] | null;
  photos: number;
}

function ReviewSummary({ values, position, polygon, photos }: ReviewSummaryProps) {
  return (
    <dl className="grid gap-3 sm:grid-cols-2">
      <Row icon={<MapPin className="h-4 w-4" />} label="Location">
        {position
          ? `${position[0].toFixed(5)}, ${position[1].toFixed(5)}`
          : '— not set'}
        {polygon && (
          <span className="ml-2 text-xs text-muted-foreground">
            · burn area ({polygon.length} pts)
          </span>
        )}
      </Row>
      <Row icon={<CalIcon className="h-4 w-4" />} label="Start">
        {values.startTime ? values.startTime.toLocaleString() : '—'}
      </Row>
      <Row icon={<CalIcon className="h-4 w-4" />} label="End">
        {values.endTime ? values.endTime.toLocaleString() : 'Ongoing'}
      </Row>
      <Row icon={<Flame className="h-4 w-4" />} label="Status">
        {values.isActive ? (
          <Badge variant="destructive">Active</Badge>
        ) : (
          <Badge variant="secondary">Resolved</Badge>
        )}
      </Row>
      <Row label="Type">{FIRE_TYPE_LABELS[values.fireType]}</Row>
      <Row label="Illegal burn">
        {values.isIllegal ? <Badge variant="destructive">Illegal</Badge> : '—'}
      </Row>
      <Row label="Linked member">{values.memberName || '—'}</Row>
      <Row label="Suburb">{values.suburb || '—'}</Row>
      <Row label="Address">{values.address || '—'}</Row>
      <Row label="Area (ha)">{values.areaSize ? Number(values.areaSize) : '—'}</Row>
      <Row label="People">{values.peopleInvolved}</Row>
      <Row label="Man hours">{values.manHours}</Row>
      <Row label="Injuries">{values.injuries}</Row>
      <Row label="Houses damaged">
        {values.housesDamagedCount} (R{Number(values.housesEstimatedDamage || 0).toLocaleString()})
      </Row>
      <Row label="Livestock injured">
        {values.livestockInjured} (R{Number(values.livestockEstimatedDamage || 0).toLocaleString()})
      </Row>
      <Row label="Mutual aid">
        {values.mutualAid && values.mutualAid.length > 0 ? (
          <span>
            {values.mutualAid
              .filter((a) => a.providerKey)
              .map((a) =>
                [a.providerName || a.providerKey, a.people ? `${a.people}p` : null]
                  .filter(Boolean)
                  .join(' · '),
              )
              .join(', ')}
          </span>
        ) : (
          '—'
        )}
      </Row>
      <Row label="Photos">{photos}</Row>
      <Row
        icon={values.isPublic ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        label="Visibility"
      >
        {values.isPublic ? 'Public' : 'Private'}
      </Row>
    </dl>
  );
}

function Row({
  icon,
  label,
  children,
}: {
  icon?: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2 text-sm">
      {icon && <span className="mt-0.5 text-muted-foreground">{icon}</span>}
      <div>
        <dt className="text-xs text-muted-foreground">{label}</dt>
        <dd>{children}</dd>
      </div>
    </div>
  );
}
